import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Flex,
  Box,
  Button,
} from '@chakra-ui/react';
import { merge } from 'lodash-es';
import PropsTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '../../../../../../service/pnotifyService';
import slPaymentService from '../../../../../../service/slPaymentService';
import { toMoney, dollarsToCents } from '../../../../../../utils/money';
import { SECTION_STATUS } from '../../../../constants';
import { useAppContext } from '../../../../context/appContext';
import ErrorPanel from '../ErrorPanel';
import Loading from '../Loading';

const AddCreditCardModal = ({ isOpen, onCancel, onComplete }) => {
  const { t } = useTranslation();
  const { merchantData, currentUser } = useAppContext();
  const [status, setStatus] = useState(SECTION_STATUS.INITIAL);
  const shoplinePaymentV2Content = useRef();

  const currencyLabel = useMemo(() => {
    const currencyCode = merchantData.base_currency_code;
    return toMoney({
      symbol: currencyCode,
      cents: dollarsToCents(1, currencyCode),
    }).label;
  }, [merchantData.base_currency_code]);

  const init = useCallback(async () => {
    setStatus(SECTION_STATUS.LOADING);
    try {
      const currency = toMoney({
        cents: dollarsToCents(1, merchantData.base_currency_code),
        currency_iso: merchantData.base_currency_code,
      });
      const amount =
        currency.currency_iso === 'TWD' ? currency.cents * 100 : currency.cents;

      const paymentConfig = merge(
        {},
        slPaymentService.getDefaultPaymentConfig(),
        {
          amount,
          paymentMethod: 'CreditCard',
          customerToken: '',
          paymentInstrument: {
            bindCard: {
              enable: true,
              protocol: {
                mustAccept: true,
                textType: {
                  paymentAgreement: true,
                },
                switchVisible: true,
              },
            },
          },
        },
      );
      const result = await slPaymentService.renderSDK({
        paymentConfig,
        elementId: '#member-credit-card-shopline-payment-v2-form',
      });
      shoplinePaymentV2Content.current = result;
      setStatus(SECTION_STATUS.SUCCESS);
    } catch (e) {
      console.error(e);
      setStatus(SECTION_STATUS.FAILURE);
    }
  }, [merchantData.base_currency_code]);

  const handleSubmit = useCallback(async () => {
    const isValid = await shoplinePaymentV2Content.current.validate();
    if (!isValid) {
      return;
    }

    try {
      setStatus(SECTION_STATUS.LOADING);
      const paySessionResult =
        await shoplinePaymentV2Content.current.getPaySession();
      const paymentInstrumentResult =
        await slPaymentService.updatePaymentInstruments({
          polling_id: currentUser._id + new Date().getTime(),
          pay_session: paySessionResult,
        });
      const payResult = await shoplinePaymentV2Content.current.pay(
        paymentInstrumentResult.data.next_action,
      );
      if (payResult?.paymentError) {
        notify(t('user.add_credit_card.fail'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        setStatus(SECTION_STATUS.FAILURE);
      } else {
        setStatus(SECTION_STATUS.SUCCESS);
        onComplete();
      }
    } catch (e) {
      console.error(e);
      notify(t('user.add_credit_card.fail'), {
        customClass: 'error',
        icon: 'fa fa-exclamation-triangle',
      });
      setStatus(SECTION_STATUS.FAILURE);
    }
  }, [currentUser._id, onComplete, t]);

  useEffect(() => {
    if (isOpen) {
      init();
    }
  }, [init, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onOverlayClick={onCancel}
      onClose={onCancel}
      isCentered
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text
            variant="sectionTitle"
            className="member-center-profile__section-title-text"
            fontSize="16px"
          >
            {t('user.add_credit_card')}
          </Text>
        </ModalHeader>
        <ModalBody>
          {status === SECTION_STATUS.FAILURE ? (
            <ErrorPanel onReload={onCancel} />
          ) : (
            <>
              {status === SECTION_STATUS.LOADING && (
                <Box mb="10px" mt="10px">
                  <Loading />
                </Box>
              )}
              <form id="member-credit-card-shopline-payment-v2-form" />
              <Text
                variant="hint"
                className="member-center-profile__hint-text"
                fontSize="12px"
                mt="5px"
                ml="20px"
              >
                {t('user.add_credit_card.remind', { label: currencyLabel })}
              </Text>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {status !== SECTION_STATUS.FAILURE && (
            <Flex justifyContent="flex-end">
              <Button
                variant="outline"
                className="member-center-profile__outline-button"
                onClick={onCancel}
                mr="10px"
              >
                {t('user.delete_credit_card.cancel')}
              </Button>
              <Button variant="fillSuccess" onClick={handleSubmit}>
                {t('user.add_credit_card.submit')}
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AddCreditCardModal.propTypes = {
  isOpen: PropsTypes.bool,
  onCancel: PropsTypes.func.isRequired,
  onComplete: PropsTypes.func.isRequired,
};

export default AddCreditCardModal;
